import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button/Button';
import './styles.css';

interface SessionExpiryModalProps {
  /** Seconds remaining before the session lapses (drives the mm:ss countdown). */
  secondsLeft: number;
  /** True while the token refresh triggered by "stay connected" is in flight. */
  renewing: boolean;
  /** Refresh the token and keep the session alive. */
  onRenew: () => void;
  /** Log out now (redirects to /login). */
  onLogout: () => void;
}

function formatMmSs(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safe / 60).toString().padStart(2, '0');
  const seconds = (safe % 60).toString().padStart(2, '0');
  return `${minutes}:${seconds}`;
}

/**
 * Blocking warning shown when the auth token is about to expire (5 min before
 * `exp`). The user can extend the session ("Seguir conectado" → token refresh)
 * or leave. If the countdown reaches zero with no action, the manager logs the
 * user out — so this modal never lets a radiologist keep working into a dead
 * session and lose their work to a surprise 401.
 */
export function SessionExpiryModal({ secondsLeft, renewing, onRenew, onLogout }: SessionExpiryModalProps) {
  const { t } = useTranslation();

  return createPortal(
    <div className="sem__backdrop" role="presentation">
      <div className="sem" role="alertdialog" aria-modal="true" aria-labelledby="sem-title">
        <div className="sem__icon" aria-hidden="true">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
          </svg>
        </div>

        <h2 id="sem-title" className="sem__title">
          {t('session.expiryTitle', 'Tu sesión está a punto de caducar')}
        </h2>

        <p className="sem__subtitle">
          {t(
            'session.expirySubtitle',
            'Por seguridad, tu sesión se cerrará automáticamente cuando termine la cuenta atrás. Renuévala para seguir trabajando sin perder tu informe.',
          )}
        </p>

        <div className="sem__countdown" aria-live="polite">
          {formatMmSs(secondsLeft)}
        </div>

        <div className="sem__actions">
          <Button variant="secondary" onClick={onLogout} disabled={renewing}>
            {t('session.logoutNow', 'Cerrar sesión')}
          </Button>
          <Button variant="primary" onClick={onRenew} disabled={renewing}>
            {renewing
              ? t('session.renewing', 'Renovando…')
              : t('session.stayConnected', 'Seguir conectado')}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default SessionExpiryModal;
